/**
 * Who holds a token, and who has made money on it.
 *
 * Both tabs are the same list read two ways, so the shaping is one function.
 * What it adds to the raw makers is the only thing this product can add: which
 * of them is the dev and which of them you follow. A ranked table of hex
 * strings is a lookup; the same table with two of the rows marked is a reason
 * to keep reading.
 */

export type RawMaker = {
  address: string
  rank?: number
  balanceCurrency: { toNumber: () => number }
  balanceInUsd: { toNumber: () => number }
  balanceInUsdPercentage: { toNumber: () => number }
  volumeInUsd: { toNumber: () => number }
  realizedPnlUsd: { toNumber: () => number }
  unrealizedPnlUsd: { toNumber: () => number }
  txCount: number
  buys: number
  sells: number
}

export type MakerRow = {
  key: string
  address: string
  rank: number
  heldTokens: number
  heldUsd: number
  sharePercent: number
  volumeUsd: number
  realisedUsd: number
  unrealisedUsd: number
  totalPnlUsd: number
  txCount: number
  buys: number
  sells: number
  isDev: boolean
  isTracked: boolean
}

const key = (address: string) => address.toLowerCase()

export const makerRows = (
  makers: ReadonlyArray<RawMaker>,
  {
    dev,
    tracked,
    limit = 50,
  }: {
    dev?: ReadonlyArray<string | undefined>
    tracked?: Iterable<string>
    limit?: number
  } = {},
): ReadonlyArray<MakerRow> => {
  const devSet = new Set(
    (dev ?? [])
      .filter((address): address is string => Boolean(address))
      .map((address) => key(address)),
  )
  const trackedSet = new Set(
    [...(tracked ?? [])].map((address) => key(address)),
  )

  return makers.slice(0, limit).map((maker, index) => {
    const realised = maker.realizedPnlUsd.toNumber()
    const unrealised = maker.unrealizedPnlUsd.toNumber()

    return {
      key: String(maker.address),
      address: String(maker.address),
      /* The server ranks holders; top traders arrive in order instead, so
         position in the list is the rank when it does not send one. */
      rank: maker.rank ?? index + 1,
      heldTokens: maker.balanceCurrency.toNumber(),
      heldUsd: maker.balanceInUsd.toNumber(),
      sharePercent: maker.balanceInUsdPercentage.toNumber(),
      volumeUsd: maker.volumeInUsd.toNumber(),
      realisedUsd: realised,
      unrealisedUsd: unrealised,
      /* What they are up overall: money taken out plus money still in. Either
         half alone flatters or damns the same wallet. */
      totalPnlUsd: sum(realised, unrealised),
      txCount: maker.txCount,
      buys: maker.buys,
      sells: maker.sells,
      isDev: devSet.has(key(String(maker.address))),
      isTracked: trackedSet.has(key(String(maker.address))),
    }
  })
}

/** A missing half is nothing, not a reason to lose the half we have. */
const sum = (left: number, right: number): number => {
  const a = Number.isFinite(left) ? left : 0
  const b = Number.isFinite(right) ? right : 0
  return a + b
}

/**
 * The share of supply at which a single holder stops being ordinary.
 *
 * The same ladder the board's rows use, so a wallet reading 12% on this table
 * and 12% in the risk panel is coloured once, the same way.
 */
export const holderTone = (sharePercent: number): 'quiet' | 'watch' | 'bad' => {
  if (!Number.isFinite(sharePercent) || sharePercent < 2) {
    return 'quiet'
  }
  return sharePercent < 5 ? 'watch' : 'bad'
}
